import { Notifications } from "@mantine/notifications";
import { defaultShouldDehydrateQuery } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { httpBatchStreamLink, loggerLink } from "@trpc/client";
import { PostHogProvider } from "posthog-js/react";
import { Suspense, useMemo } from "react";
import { CookiesProvider } from "react-cookie";
import { RouterProvider } from "react-router-dom";
import * as superjson from "superjson";
import {
  createNoanAsyncStoragePersister,
  noanAsyncStorageCacheBuster,
} from "./asyncStoragePersister";
import { AuthOptionalContext } from "./auth/AuthContext";
import {
  useSupabaseAuth,
  useSupabaseCachedSessionIdentifier,
} from "./auth/supabase";
import { useOptionalAuthContext } from "./auth/useAuthContext";
import { ConnectionStateMonitor } from "./components/ConnectionStateMonitor";
import { FullPageLoader } from "./components/FullPageLoader";
import { ThemeProvider } from "./components/ThemeProvider";
import { postHogApiKey, postHogOptions, trpcEndpoint } from "./config";
import { routeDefinitions } from "./pages";
import { queryClient } from "./queryClient";
import { createSentryBrowserRouter } from "./sentry";
import { trpc } from "./utils/trpc";

// Init page routes:
const router = createSentryBrowserRouter(routeDefinitions);

export function AppContainer() {
  const {
    user,
    signOut,
    isLoading: isLoadingUser,
    authError,
    jwtBearerToken,
    hasThirdPartyProvider,
    mustSetPassword,
    mustCompletePostSignup,
  } = useSupabaseAuth();

  return (
    <AuthOptionalContext.Provider
      value={{
        user: user!,
        signOut,
        isLoadingUser,
        authError,
        jwtBearerToken,
        hasThirdPartyProvider,
        mustSetPassword,
        mustCompletePostSignup,
      }}
    >
      <App />
    </AuthOptionalContext.Provider>
  );
}

const asyncStoragePersister = createNoanAsyncStoragePersister();

export function App() {
  const { jwtBearerToken } = useOptionalAuthContext();

  /**
   * We need this as early as possible - in all the contexts we care about it'll be
   * set, and is available before the first render, so that we can immediately hydrate
   * the cache based on the current active user.
   */
  const cachedSessionIdentifier = useSupabaseCachedSessionIdentifier();

  const trpcClient = useMemo(
    () =>
      trpc.createClient({
        links: [
          ...(import.meta.env.VITE_TRPC_DEBUG === "true" ? [loggerLink()] : []),
          httpBatchStreamLink({
            url: trpcEndpoint,
            transformer: superjson,
            fetch(url, options) {
              return fetch(url, { ...options, credentials: "include" });
            },
            headers() {
              return {
                ...(jwtBearerToken == null
                  ? {}
                  : {
                      Authorization: jwtBearerToken,
                    }),
              };
            },
          }),
        ],
      }),
    [jwtBearerToken],
  );

  return (
    <PostHogProvider apiKey={postHogApiKey} options={postHogOptions}>
      <Suspense fallback={<FullPageLoader />}>
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{
            maxAge: 1000 * 60 * 60 * 24, // 24 hours,
            persister: asyncStoragePersister,
            buster: `${noanAsyncStorageCacheBuster}_${cachedSessionIdentifier || "_"}`,
            dehydrateOptions: {
              /** Queries tagged with meta.noCache will not be saved into the cache: */
              shouldDehydrateQuery: (query) => {
                if (query.options.meta?.noCache) {
                  return false;
                }

                /**
                 * Use the default behavior, which handles things like not hydrating
                 * queries in error state.
                 */
                return defaultShouldDehydrateQuery(query);
              },
            },
          }}
        >
          <trpc.Provider client={trpcClient} queryClient={queryClient}>
            <CookiesProvider>
              <ThemeProvider>
                <Notifications position="bottom-center" />
                <RouterProvider router={router} />
                <ConnectionStateMonitor />
              </ThemeProvider>
            </CookiesProvider>

            <ReactQueryDevtools
              position="top"
              buttonPosition="top-left"
              client={queryClient}
              hideDisabledQueries
            />
          </trpc.Provider>
        </PersistQueryClientProvider>
      </Suspense>
    </PostHogProvider>
  );
}
